import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ThemeToggle from '../../components/ThemeToggle';

function MasterAdminTenants() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const token = localStorage.getItem('masterAdminToken');
  const user = JSON.parse(localStorage.getItem('masterAdminUser') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/master-admin/login');
      return;
    }
    fetchTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, search, tierFilter, statusFilter]);

  const fetchTenants = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (tierFilter) params.append('tier', tierFilter);
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/tenants?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('masterAdminToken');
        navigate('/master-admin/login');
        return;
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setTenants(data.tenants);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('masterAdminToken');
    localStorage.removeItem('masterAdminUser');
    navigate('/master-admin/login');
  };

  const getTierBadgeClass = (tier) => {
    const classes = {
      free_trial: 'badge-secondary',
      starter: 'badge-info',
      pro: 'badge-primary',
      enterprise: 'badge-success'
    };
    return classes[tier] || 'badge-secondary';
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      trial: 'badge-warning',
      active: 'badge-success',
      past_due: 'badge-danger',
      suspended: 'badge-danger',
      cancelled: 'badge-secondary'
    };
    return classes[status] || 'badge-secondary';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="admin-page master-admin-page">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-page master-admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>DeedStack Platform Admin</h1>
        </div>
        <div className="admin-header-right">
          <span className="admin-user-name">{user.firstName} {user.lastName}</span>
          <ThemeToggle />
          <button onClick={handleLogout} className="btn btn-secondary btn-small">
            Logout
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="admin-nav">
        <Link to="/master-admin/dashboard" className="admin-nav-link">Dashboard</Link>
        <Link to="/master-admin/tenants" className="admin-nav-link active">Tenants</Link>
        <Link to="/master-admin/calendar" className="admin-nav-link">Calendar</Link>
        <Link to="/master-admin/emails" className="admin-nav-link">Emails</Link>
        <Link to="/master-admin/billing" className="admin-nav-link">Billing</Link>
        <Link to="/master-admin/financials" className="admin-nav-link">Financials</Link>
        <Link to="/master-admin/settings" className="admin-nav-link">Settings</Link>
      </nav>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-container">
          <div className="admin-section">
          <div className="admin-section-header">
            <h2>Tenants ({tenants.length})</h2>
            <Link to="/master-admin/tenants/new" className="btn btn-primary">
              Add Tenant
            </Link>
          </div>

          {error && <div className="error-message">{error}</div>}

          {/* Filters */}
          <div className="admin-filters" style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search company, email, subdomain..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: '200px' }}
            />
            <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
              <option value="">All Tiers</option>
              <option value="free_trial">Free Trial</option>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="trial">Trial</option>
              <option value="active">Active</option>
              <option value="past_due">Past Due</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Tenants Table */}
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Subdomain</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th>Usage</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                      No tenants found
                    </td>
                  </tr>
                ) : (
                  tenants.map((tenant) => (
                    <tr key={tenant.id}>
                      <td>
                        <strong>{tenant.companyName}</strong>
                        {tenant.billingExempt && (
                          <span style={{
                            marginLeft: '8px',
                            padding: '2px 6px',
                            fontSize: '0.7em',
                            background: '#3b82f6',
                            color: 'white',
                            borderRadius: '4px',
                            verticalAlign: 'middle'
                          }} title={tenant.billingExemptReason || 'Billing exempt account'}>
                            EXEMPT
                          </span>
                        )}
                      </td>
                      <td>
                        <div>{tenant.contactName || '-'}</div>
                        <div style={{ fontSize: '0.85em', color: 'var(--color-text-light)' }}>{tenant.contactEmail}</div>
                      </td>
                      <td>
                        {tenant.subdomain ? (
                          <code>{tenant.subdomain}.deedstack.com</code>
                        ) : '-'}
                      </td>
                      <td>
                        <span className={`badge ${getTierBadgeClass(tenant.subscriptionTier)}`}>
                          {tenant.subscriptionTier?.replace('_', ' ')}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(tenant.subscriptionStatus)}`}>
                          {tenant.subscriptionStatus}
                        </span>
                        {/* Show Trial Expired badge */}
                        {tenant.suspensionReason === 'trial_expired' && (
                          <span style={{
                            marginLeft: '6px',
                            padding: '2px 6px',
                            fontSize: '0.7em',
                            background: '#dc2626',
                            color: 'white',
                            borderRadius: '4px',
                            verticalAlign: 'middle'
                          }}>
                            TRIAL EXPIRED
                          </span>
                        )}
                        {/* Show Trial Expiring warning */}
                        {tenant.subscriptionTier === 'free_trial' &&
                         tenant.subscriptionStatus !== 'suspended' &&
                         tenant.trialEndsAt &&
                         (() => {
                           const daysLeft = Math.ceil((new Date(tenant.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24));
                           if (daysLeft <= 3 && daysLeft > 0) {
                             return (
                               <span style={{
                                 marginLeft: '6px',
                                 padding: '2px 6px',
                                 fontSize: '0.7em',
                                 background: '#f59e0b',
                                 color: 'white',
                                 borderRadius: '4px',
                                 verticalAlign: 'middle'
                               }} title={`Trial ends ${new Date(tenant.trialEndsAt).toLocaleDateString()}`}>
                                 {daysLeft}d LEFT
                               </span>
                             );
                           }
                           return null;
                         })()
                        }
                      </td>
                      <td>
                        <div style={{ fontSize: '0.85em' }}>
                          {tenant.activeLoans} loans, {tenant.totalProperties} props
                        </div>
                      </td>
                      <td>{formatDate(tenant.createdAt)}</td>
                      <td>
                        <Link
                          to={`/master-admin/tenants/${tenant.id}`}
                          className="btn btn-small btn-secondary"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </main>
    </div>
  );
}

export default MasterAdminTenants;
